import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { inflate } from 'zlib';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { OA, SHEETS } from '../../ngrx/category-types';

import { EOD } from '../models';

import { allEODFields } from '../../ngrx/graphql';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class EODService {
  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private sanitizer        : DomSanitizer,
    private store            : Store<any>
  ) {}

  private invokeDownloadEODReport(filename: string): Observable<any> {
    return new Observable(observer => {
      
      this.credentialService.lambda.invoke({
        FunctionName: environment.downloadEODReportFunction,
        Payload: JSON.stringify(filename)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeEOD(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.EODFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      );
    });
  }

  private invokeEODMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.EODFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    );
  }

  private invokeProcessEOD(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.processEODFunction,
        InvocationType: "Event",
        Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
          observer.error(err);
        } else {
          if (data.Payload) {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          } else {
            observer.next(`statusCode - ${data.StatusCode}`)
          }
        }
      }
      );
    });
  }

  deleteEOD(id: string): void {
    const variables = { id };
    const query = `mutation ($id: ID!) {
      deleteEOD(id: $id) {
        id
      }
    }`;

    this.invokeEODMutation({ query, variables });
  }

  downloadReport(filename: string): void {
    console.log(`downloading report: ${filename}`);
    this.invokeDownloadEODReport(filename)
      .take(1)
      .subscribe(
        url => {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.style.display = 'none';
          console.log(`Downloading EOD report ${filename} from url: ${url}`);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
        err => console.log(err)
      );
  }

  getEOD(id: string): Observable<any> {
    const query = `{
      eod(id: "${id}") {
        ${allEODFields}
      }
    }`;

    return this.invokeEOD({ query });
  }

  getEODHistory(): Observable<any> {
    const query = `{
      eodHistory(param: "category", value: "${SHEETS}") {
        ${allEODFields}
      }
    }`;

    return this.invokeEOD({ query });
  }

  getEODHistoryList(): Observable<EOD[]> {
    return this.store.select('eod', 'eodHistory');
  }

  getEODHistoryRange(category: string, from: string, to: string): Observable<any> {
    const query = `{
      eodHistoryRange(category: "${category}", from: "${from}", to: "${to}") {
        ${allEODFields}
      }
    }`;

    return this.invokeEOD({ query });
  }

  getEODHistoryView(): Observable<any> {
    return this.store.select('eod', 'eodHistoryView');
  }

  getLastEODRanToday(): Observable<boolean> {
    return this.store.select('eod', 'lastEODRanToday');
  }

  getOAEODHistory(): Observable<any> {
    const query = `{
      eodHistory(param: "category", value: "${OA}") {
        ${allEODFields}
      }
    }`;

    return this.invokeEOD({ query });
  }

  processEOD(id: string = null, category: string = '', lastOfDay: boolean = false, carrier: string = ''): void {
    // console.log("USER STARTED EOD: ",this.authService.getCurrentUser());
    this.invokeProcessEOD({ id, lastOfDay, category, user: this.authService.getCurrentUser(), carrier })
      .take(1)
      .subscribe(
        (res: any) => console.log(`EOD process started: ${JSON.stringify(res)}`),
        (err: Error) => console.log(`Error when attempting to start EOD process: ${JSON.stringify(err)}`)
      );
  }
}


// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/services/eod.service.ts